import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
  Badge,
  IconButton,
  Spinner,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import { X, Plus, Search, Flag, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  getTopMissedQuestionsForAdminRequest,
  updateQuestionTagsRequest,
} from "../../../modules/admin-modules/questions-bank/Actions";
import { getAllTagsRequest } from "../../../modules/admin-modules/tags/Actions";

const TagMissedQuestions = () => {
  const dispatch = useDispatch();
  const baseUrl = "https://asquera.com/asquera";

  const topMissedQuestions = useSelector(
    (state) => state.questionsBank.topMissedQuestions
  );
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  const tags = useSelector((state) => state.adminTags.tags);
  const tagsLoading = useSelector((state) => state.adminTags.isLoading);
  const updateLoading = useSelector(
    (state) => state.questionsBank.updateTagsLoading
  );

  const [questionTags, setQuestionTags] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    dispatch(getTopMissedQuestionsForAdminRequest());
    dispatch(getAllTagsRequest());
  }, [dispatch]);

  // Initialize question tags from the questions data
  useEffect(() => {
    if (topMissedQuestions && topMissedQuestions.length > 0) {
      setQuestionTags((prevTags) => {
        const newTags = { ...prevTags };
        topMissedQuestions.forEach((item) => {
          const question = item.question || item;
          if (question.id) {
            // Only initialize if not already set (preserve user's local changes)
            if (!newTags[question.id]) {
              newTags[question.id] = question.tags || [];
            } else {
              // Update with latest tags from server if question was just updated
              const serverTags = question.tags || [];
              const currentTagIds = (newTags[question.id] || []).map(t => t.id).sort();
              const serverTagIds = serverTags.map(t => t.id).sort();
              // If IDs match, update the tag objects (in case tag names changed)
              if (JSON.stringify(currentTagIds) === JSON.stringify(serverTagIds)) {
                newTags[question.id] = serverTags;
              }
            }
          }
        });
        return newTags;
      });
    }
  }, [topMissedQuestions]);

  const handleAddTag = (questionId, tagId) => {
    const currentTags = questionTags[questionId] || [];
    if (!currentTags.find((tag) => tag.id === tagId)) {
      const tagToAdd = tags.find((tag) => tag.id === tagId);
      if (tagToAdd) {
        setQuestionTags({
          ...questionTags,
          [questionId]: [...currentTags, tagToAdd],
        });
      }
    }
  };

  const handleRemoveTag = (questionId, tagId) => {
    const currentTags = questionTags[questionId] || [];
    setQuestionTags({
      ...questionTags,
      [questionId]: currentTags.filter((tag) => tag.id !== tagId),
    });
  };

  const handleUpdateTags = (questionId) => {
    const tagsToUpdate = questionTags[questionId] || [];
    const tagIds = tagsToUpdate.map((tag) => tag.id);
    dispatch(updateQuestionTagsRequest(questionId, tagIds));
  };

  const tagsOptions = tags?.map((tag) => ({
    value: tag.id,
    label: tag.name,
  }));

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  const filteredQuestions = topMissedQuestions?.filter((item) => {
    const question = item.question || item;
    const questionText = question.question || "";
    return questionText.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  // Calculate pagination
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQuestions = filteredQuestions.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset to page 1 when items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const renderQuestionCard = (item, index) => {
    const question = item.question || item;
    const wrongCount = item.wrong_count || 0;
    const questionId = question.id;
    const currentTags = questionTags[questionId] || [];
    const availableTags = tags?.filter(
      (tag) => !currentTags.find((t) => t.id === tag.id)
    );
    // Calculate the actual question number (considering pagination)
    const questionNumber = startIndex + index + 1;

    return (
      <Box
        key={questionId || index}
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.200"
        p={6}
        mb={6}
      >
        <Flex justify="space-between" align="start" mb={4}>
          <HStack spacing={3}>
            <Badge
              colorScheme="orange"
              px={3}
              py={1}
              borderRadius="lg"
              fontSize="sm"
            >
              #{questionNumber}
            </Badge>
            {wrongCount > 0 && (
              <Badge colorScheme="red" px={3} py={1} borderRadius="lg" fontSize="sm">
                {wrongCount} students missed
              </Badge>
            )}
          </HStack>
          <IconButton
            icon={<Flag size={16} />}
            size="sm"
            variant="ghost"
            aria-label="Flag question"
          />
        </Flex>

        <Box mb={4}>
          <Text
            fontSize="md"
            fontWeight="medium"
            color="gray.900"
            dangerouslySetInnerHTML={{ __html: question.question }}
          />
        </Box>

        {question.image && (
          <Box mb={4}>
            <img
              src={`${baseUrl}/${question.image}`}
              alt="Question"
              style={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: "200px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            />
          </Box>
        )}

        <VStack align="stretch" spacing={4} mt={4}>
          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
              Active Tags:
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {currentTags.length > 0 ? (
                currentTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    colorScheme="teal"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    {tag.name}
                    <IconButton
                      icon={<X size={12} />}
                      size="xs"
                      variant="ghost"
                      aria-label="Remove tag"
                      onClick={() => handleRemoveTag(questionId, tag.id)}
                      _hover={{ bg: "teal.600", color: "white" }}
                    />
                  </Badge>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No tags assigned
                </Text>
              )}
            </HStack>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
              Add Tag:
            </Text>
            <HStack spacing={2}>
              <Box flex={1}>
                <ReactSelect
                  options={tagsOptions}
                  onChange={(selected) => {
                    if (selected) {
                      handleAddTag(questionId, selected.value);
                    }
                  }}
                  styles={colourStyles}
                  placeholder="Select a tag..."
                  isClearable
                />
              </Box>
            </HStack>
          </Box>

          {availableTags && availableTags.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                Suggested Tags:
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                {availableTags.slice(0, 5).map((tag) => (
                  <Badge
                    key={tag.id}
                    colorScheme="gray"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                    cursor="pointer"
                    _hover={{ bg: "gray.300" }}
                    onClick={() => handleAddTag(questionId, tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </HStack>
            </Box>
          )}

          <Flex justify="flex-end" mt={4}>
            <Button
              colorScheme="orange"
              leftIcon={<Plus size={16} />}
              onClick={() => handleUpdateTags(questionId)}
              isLoading={updateLoading}
              loadingText="Updating..."
              bg="orange.500"
                color="white"
                mt="20px"
                borderRadius="xl"
                fontSize="18px"
                fontWeight="700"
                _hover={{
                  bg: "orange.600",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(255, 140, 0, 0.3)"
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.3s ease"
                boxShadow="0 4px 15px rgba(255, 140, 0, 0.2)"
                fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              Update Tags
            </Button>
          </Flex>
        </VStack>
      </Box>
    );
  };

  return (
    <Layout>
      <VStack w="100%" my={{ base: "50px", md: "30px" }}>
      <Flex
        alignItems="center"
        w="100%"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Heading size="lg" color="gray.800">
          Tag Missed Questions
        </Heading>

        <Text fontSize="md" color="gray.600">
          Review and tag top missed questions by students
        </Text>
        <LogoutMenu />
      </Flex>
      </VStack>
      <VStack align="stretch" spacing={6} p={6}>


        <Box>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Search size={20} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
              borderRadius="lg"
            />
          </InputGroup>
        </Box>

        {isLoading || tagsLoading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner size="xl" color="teal.500" />
          </Flex>
        ) : filteredQuestions && filteredQuestions.length > 0 ? (
          <>
            <Stack spacing={4}>
              {paginatedQuestions.map((item, index) => renderQuestionCard(item, index))}
            </Stack>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Flex
                justify="space-between"
                align="center"
                flexWrap="wrap"
                gap={4}
                mt={6}
                pt={6}
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    Items per page:
                  </Text>
                  <Select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    size="sm"
                    w="80px"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Select>
                </HStack>

                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredQuestions.length)} of{" "}
                    {filteredQuestions.length} questions
                  </Text>
                </HStack>

                <HStack spacing={2}>
                  <Button
                    size="sm"
                    leftIcon={<ChevronLeft size={16} />}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    isDisabled={currentPage === 1}
                    variant="outline"
                  >
                    Previous
                  </Button>

                  <HStack spacing={1}>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNum = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <Button
                            key={pageNum}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            colorScheme={currentPage === pageNum ? "orange" : "gray"}
                            variant={currentPage === pageNum ? "solid" : "outline"}
                            minW="40px"
                          >
                            {pageNum}
                          </Button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <Text key={pageNum} px={2} color="gray.500">
                            ...
                          </Text>
                        );
                      }
                      return null;
                    })}
                  </HStack>

                  <Button
                    size="sm"
                    rightIcon={<ChevronRight size={16} />}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    isDisabled={currentPage === totalPages}
                    variant="outline"
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            )}
          </>
        ) : (
          <Box textAlign="center" py={10}>
            <Text fontSize="lg" color="gray.500">
              No missed questions found
            </Text>
          </Box>
        )}
      </VStack>
    </Layout>
  );
};

export default TagMissedQuestions;

